import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import { showSuccessToast } from "@src/appV2/lib/Notifications";
import {
  ChartingCredentialSoftware,
  type ChartingCredentialSource,
  type CredentialsFormData,
  credentialsFormSchema,
  WORKER_CREDENTIAL_TYPE,
} from "@src/appV2/WorkerCredentials/api/schema";
import { useCreateWorkerCredentials } from "@src/appV2/WorkerCredentials/api/useCreateWorkerCredentials";
import { useSetupShareCredentialsForm } from "@src/appV2/WorkerCredentials/api/useSetupShareCredentialsForm";
import { Text, Title } from "@src/lib/ui-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import { environmentConfig } from "../environment";
import {
  getWorkerExternalIntegrationConfigurationUrl,
  useGetWorkerExternalIntegrationConfiguration,
} from "../ExternalIntegrations/api/useGetWorkerExternalIntegrationConfiguration";
import { useUpdateWorkerExternalIntegrationConfiguration } from "../ExternalIntegrations/api/useUpdateWorkerExternalIntegrationConfiguration";
import { ExtendedExternalIntegrationTypes } from "../ExternalIntegrations/types";
import { useUpdateDefaultChartingPassword } from "../FacilityProfile/api/useUpdateDefaultChartingPassword";
import { APP_V2_USER_EVENTS, useToast } from "../lib";
import { AttendanceOnDemandTimeclockCredentials } from "./VendorTimeclockCredentials/AttendanceOnDemandTimeclockCredentials";
import { TimeclockComplianceCredentials } from "./VendorTimeclockCredentials/TimeclockComplianceCredentials";
import { UKGTimeclockCredentials } from "./VendorTimeclockCredentials/UKGTimeclockCredentials";

interface ShareCredentialsModalProps {
  workerId: string;
  workplaceId: string;
  onClose: () => void;
  onSuccess: () => void;
  source: ChartingCredentialSource;
  workerName: string | undefined;
}

export function ShareCredentialsModal({
  workerId,
  workplaceId,
  onClose,
  onSuccess,
  source,
  workerName,
}: ShareCredentialsModalProps) {
  const { mutateAsync: createWorkerCredentials } = useCreateWorkerCredentials();
  const { mutateAsync: updateDefaultChartingPassword } = useUpdateDefaultChartingPassword();
  const { mutateAsync: updateWorkerExternalIntegrationConfiguration } =
    useUpdateWorkerExternalIntegrationConfiguration();
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();

  const {
    isLoading,
    refetchChartingCredentials,
    timeclockVendor,
    showCharting,
    chartingCredentials,
    timeclockCredentials,
    defaultChartingSoftware,
    setDefaultChartingSoftware,
    initialChartingPassword,
    shouldUpdateDefaultChartingPassword,
    setShouldUpdateDefaultChartingPassword,
    showPassword,
    setShowPassword,
    showPin,
    setShowPin,
    missingInitialCredentials,
  } = useSetupShareCredentialsForm({ workplaceId, workerId });

  const {
    data: existingConfiguration,
    isError: isExternalIdError,
    error: externalIdError,
    isLoading: isLoadingConfiguration,
  } = useGetWorkerExternalIntegrationConfiguration({
    workerId,
    workplaceId,
  });

  const is404Error = externalIdError?.response?.status === 404;
  const externalIdErrorPlaceholder = is404Error
    ? "Professional not onboarded yet"
    : "An error occurred loading person number";

  const initialFormValues = useMemo<CredentialsFormData>(
    () => ({
      chartingSoftware:
        chartingCredentials?.software ??
        defaultChartingSoftware ??
        ChartingCredentialSoftware.POINT_CLICK_CARE,
      username: chartingCredentials?.username ?? "",
      password: initialChartingPassword,
      timeclockPin: timeclockCredentials?.pin ?? "",
      timeclockBadge: timeclockCredentials?.badgeNumber ?? "",
      externalId: existingConfiguration?.attributes.externalId ?? undefined,
    }),
    [
      chartingCredentials,
      timeclockCredentials,
      defaultChartingSoftware,
      initialChartingPassword,
      existingConfiguration,
    ]
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CredentialsFormData>({
    resolver: zodResolver(credentialsFormSchema),
    values: initialFormValues,
  });

  const selectedSoftware = watch("chartingSoftware");

  async function onSubmit({
    username,
    password,
    timeclockPin,
    timeclockBadge,
    externalId,
  }: CredentialsFormData) {
    try {
      setDefaultChartingSoftware(selectedSoftware);
      if (shouldUpdateDefaultChartingPassword) {
        await updateDefaultChartingPassword({
          workplaceId,
          defaultChartingPassword: password,
        });
      }

      await createWorkerCredentials({
        data: {
          type: WORKER_CREDENTIAL_TYPE,
          attributes: {
            workerId,
            workplaceId,
            chartingCredentials: {
              software: selectedSoftware,
              username,
              password,
              source,
            },
            timeclockCredentials: {
              pin: timeclockPin ?? "",
              badgeNumber: timeclockBadge ?? "",
            },
          },
        },
      });

      if (isDefined(existingConfiguration) && isDefined(externalId)) {
        await updateWorkerExternalIntegrationConfiguration({
          ...existingConfiguration.attributes,
          externalId,
        });
        logEvent(APP_V2_USER_EVENTS.EXTERNAL_ID_UPDATED, {
          workerId,
          workplaceId,
        });
      }

      showSuccessToast("Credentials updated successfully");
      onSuccess();
      onClose();
      // Invalidate related worker credentials queries
      await queryClient.invalidateQueries({
        queryKey: [
          `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/worker-credentials`,
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [getWorkerExternalIntegrationConfigurationUrl()],
      });
      await refetchChartingCredentials();
    } catch {
      showErrorToast("Failed to share credentials, please try again.");
    }
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleTogglePinVisibility = () => {
    setShowPin(!showPin);
  };

  if (isLoadingConfiguration) {
    return (
      <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
        <DialogContent>
          <Stack alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress />
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle component="div">
        <Stack direction="column" spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Title component="h2" variant="h3">
              {missingInitialCredentials ? "Share Credentials" : "Edit Credentials"}
            </Title>
            <IconButton
              aria-label="close"
              sx={(theme) => ({ color: theme.palette.grey[500], paddingX: 0 })}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {workerName && (
            <Text variant="body2" color="text.primary">
              {workerName}
            </Text>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {showCharting && (
              <>
                <Controller
                  control={control}
                  name="chartingSoftware"
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="software-label">Charting Software</InputLabel>
                      <Select labelId="software-label" label="Charting Software" {...field}>
                        {Object.values(ChartingCredentialSoftware).map((value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <TextField
                  fullWidth
                  label="Charting Username"
                  // FIXME: Automatically disabled during ESLint config update
                  error={Boolean(errors.username)}
                  helperText={errors.username?.message}
                  inputProps={{ "data-dd-privacy": "mask" }}
                  {...register("username")}
                />
                <TextField
                  fullWidth
                  label="Charting Password"
                  type={showPassword ? "text" : "password"}
                  // FIXME: Automatically disabled during ESLint config update
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message}
                  inputProps={{ "data-dd-privacy": "mask" }}
                  {...register("password")}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          edge="end"
                          onClick={handleTogglePasswordVisibility}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shouldUpdateDefaultChartingPassword}
                      onChange={(_, checked) => {
                        setShouldUpdateDefaultChartingPassword(checked);
                      }}
                    />
                  }
                  label="Save as workplace default password"
                />
              </>
            )}

            {timeclockVendor === ExtendedExternalIntegrationTypes.UKG && (
              <UKGTimeclockCredentials
                register={register}
                errors={errors}
                externalIdErrorPlaceholder={
                  isExternalIdError ? externalIdErrorPlaceholder : undefined
                }
              />
            )}

            {timeclockVendor === ExtendedExternalIntegrationTypes.ATTENDANCE_ON_DEMAND && (
              <AttendanceOnDemandTimeclockCredentials
                register={register}
                errors={errors}
                showPin={showPin}
                externalIdErrorPlaceholder={
                  isExternalIdError ? externalIdErrorPlaceholder : undefined
                }
                onTogglePinVisibility={handleTogglePinVisibility}
              />
            )}

            {timeclockVendor === ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE && (
              <TimeclockComplianceCredentials register={register} errors={errors} />
            )}

            <Button
              variant="contained"
              disabled={isSubmitting}
              sx={{ mt: 2 }}
              onClick={handleSubmit(onSubmit)}
            >
              {missingInitialCredentials ? "Share credentials" : "Save credentials"}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
